import { Component, Entity, Query, Scene, Vector } from "excalibur";
import { GetPlayable } from "../constants";
import { Playable, ResourceKind, GameResource } from "../types";
import { TileComponent, tileKey, TilePositionManager } from "./tile";

export type BuildError = {
  playable: Playable;
  missingUnlocks: ResourceKind[];
  missingResources: {
    resource: GameResource;
    required: number;
    available: number;
  }[];
};

export class CombinableComponent extends Component {
  tileManager: TilePositionManager | undefined;

  get matchName() {
    return this.playable.combines.ownMatchString;
  }

  combine() {
    this.onCombine();
  }

  constructor(
    public playable: Playable,
    private onCombine: () => void,
  ) {
    super();
  }
}

export class CombinableManager {
  query: Query<typeof CombinableComponent>;

  constructor(
    public scene: Scene,
    public tileManager: TilePositionManager,
  ) {
    this.query = this.scene.world.query([CombinableComponent]);

    this.query.entityAdded$.subscribe((e) => {
      const combinableComponent = e.get(CombinableComponent);
      combinableComponent.tileManager = this.tileManager;
    });

    this.query.entityRemoved$.subscribe((e) => {
      const combinableComponent = e.get(CombinableComponent);
      combinableComponent.tileManager = undefined;
    });
  }
}

// Can combine
//
// Can build

// if combinable but can't combine, need to return error to show error in ux

function canCombine(
  playable: Playable,
  neighbors: Playable[],
): Playable | undefined {
  if (neighbors.length >= 2 && playable.combines.into)
    return GetPlayable(playable.combines.into);
  return undefined;
}

export function findBuildableOption(
  playable: Playable,
  pos: Vector,
  neighbors: (pos: Vector) => TileComponent[],
  canBuild: (
    playable: Playable,
    matchingNeighbors?: Entity<CombinableComponent>[],
  ) =>
    | {
        ok: true;
        error: undefined;
      }
    | {
        ok: false;
        error: BuildError;
      },
):
  | {
      matchingNeighbors: Entity<CombinableComponent>[];
      buildError: BuildError;
      next: undefined;
      buildChain: string[];
    }
  | {
      matchingNeighbors: Entity<CombinableComponent>[];
      next: Playable;
      buildError: undefined;
      buildChain: string[];
    } {
  // First, find the complete combination chain without checking affordability
  const fullChain: {
    level: Playable;
    neighbors: Entity<CombinableComponent>[];
  }[] = [];

  let currentPlayable = playable;
  const allNeighbors: Entity<CombinableComponent>[] = [];
  const buildChain: string[] = [];

  while (true) {
    const matches = findMatchingNeighbors(
      currentPlayable.combines.withMatchStrings,
      pos,
      neighbors,
    );
    if (matches.length === 0) break;

    const combinesInto = canCombine(
      currentPlayable,
      matches.map((e) => e.get(CombinableComponent).playable),
    );

    if (!combinesInto) break;
    buildChain.push(currentPlayable.label);
    allNeighbors.push(...matches);

    fullChain.push({
      level: combinesInto,
      neighbors: [...allNeighbors],
    });

    currentPlayable = combinesInto;
  }

  // Now work backwards through the chain to find the highest affordable combination
  for (let i = fullChain.length - 1; i >= 0; i--) {
    const { level, neighbors } = fullChain[i];
    const buildInfo = canBuild(level, neighbors);

    if (buildInfo.ok) {
      return {
        matchingNeighbors: neighbors,
        next: level,
        buildError: undefined,
        buildChain,
      };
    } else {
      // If this is the highest level we attempted, return its error
      if (i === fullChain.length - 1) {
        // Include the build chain up to the failed level
        const chainToFailure = buildChain.slice(0, i + 1);
        chainToFailure.push(level.label);
        return {
          matchingNeighbors: neighbors,
          buildError: buildInfo.error,
          next: undefined,
          buildChain: chainToFailure,
        };
      }
    }
  }

  // No combinations found, check if we can build the base building
  const baseBuildResult = canBuild(playable, []);
  if (!baseBuildResult.ok) {
    return {
      matchingNeighbors: [],
      buildError: baseBuildResult.error,
      next: undefined,
      buildChain: [],
    };
  }

  return {
    matchingNeighbors: [],
    next: playable,
    buildError: undefined,
    buildChain: [],
  };
}

export function findMatchingNeighbors(
  matchNames: string[],
  pos: Vector,
  neighbors: (pos: Vector) => TileComponent[],
): Entity<CombinableComponent>[] {
  // Check for matching neighbors to updates
  const checked = new Set<string>([tileKey(pos)]);
  const found: Entity<CombinableComponent>[] = [];
  const nearby = neighbors(pos).filter((n) =>
    matchNames.includes(n.owner?.get(CombinableComponent)?.matchName ?? ""),
  );

  while (nearby.length) {
    const top = nearby.pop();
    if (!top) continue;
    const key = tileKey(top.pos);
    if (checked.has(key)) continue;
    checked.add(key);
    if (top.owner?.has(CombinableComponent)) found.push(top.owner);

    nearby.push(
      ...neighbors(top.pos).filter(
        (n) =>
          !checked.has(tileKey(n.pos.x, n.pos.y)) &&
          matchNames.includes(
            n.owner?.get(CombinableComponent)?.matchName ?? "",
          ),
      ),
    );
  }
  return found;
}
